import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

let cartRequest = null;
let lastTotal = null;

function getCart(force = false) {
  if (!cartRequest || force) {
    cartRequest = fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );
  }
  return cartRequest;
}

function formatMoney(cents, currency) {
  const locale = document.documentElement.lang || 'en';
  const zeroDecimal = ['IDR', 'JPY', 'KRW', 'VND', 'CLP', 'HUF', 'TWD', 'ISK'].includes(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: zeroDecimal ? 0 : 2,
    }).format(cents / 100);
  } catch (e) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

class FreeShippingUpsell extends HTMLElement {
  #abort;
  #observer;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    // The theme re-syncs the drawer HTML from the server after load and after cart changes.
    // In non-shop currencies the server leaves the message empty, so re-apply whenever the DOM is rewritten.
    this.#observer = new MutationObserver(() => {
      if (lastTotal !== null) this.render(lastTotal);
    });
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });

    this.refresh(false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #onCartUpdate = () => this.refresh(true);

  async refresh(force) {
    try {
      const cart = await getCart(force);
      lastTotal = Number(cart.total_price) || 0;
      this.render(lastTotal);
    } catch (error) {
      console.error('[free-shipping-upsell]', error);
    }
  }

  render(total) {
    const currency = window.Shopify?.currency?.active || this.dataset.currency;
    // Fixed = an exact per-currency goal from the snippet config (already in the active currency).
    const rate = this.dataset.thresholdFixed === 'true' ? 1 : Number(window.Shopify?.currency?.rate) || 1;
    const threshold = Number(this.dataset.threshold) * rate;
    const remaining = Math.max(0, threshold - total);
    const pct = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;

    const fill = this.querySelector('[data-fill]');
    if (fill && fill.style.width !== `${pct}%`) fill.style.width = `${pct}%`;

    const message = this.querySelector('[data-message]');
    if (!message) return;

    const text =
      remaining > 0
        ? (this.dataset.textProgress || '').replace('{amount}', formatMoney(remaining, currency))
        : this.dataset.textReached || '';

    if (message.textContent !== text) message.textContent = text;
    this.classList.toggle('fsu--reached', remaining <= 0);
  }

  #onChange = (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select || !(select instanceof HTMLSelectElement)) return;
    const option = select.selectedOptions[0];
    const price = select.closest('.fsu__card')?.querySelector('[data-price]');
    if (option?.dataset.price && price) price.textContent = option.dataset.price;
  };

  #onClick = (event) => {
    const arrow = event.target.closest('[data-dir]');
    if (arrow) {
      const track = this.querySelector('[data-track]');
      const card = track?.querySelector('.fsu__card');
      if (track && card) {
        track.scrollBy({ left: Number(arrow.dataset.dir) * (card.offsetWidth + 16), behavior: 'smooth' });
      }
      return;
    }
    const button = event.target.closest('[data-add]');
    if (button) this.#addToCart(button);
  };

  async #addToCart(button) {
    const card = button.closest('.fsu__card');
    const variantId = card?.querySelector('[data-variant-select]')?.value;
    if (!variantId) return;

    button.disabled = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();
      if (data.status) {
        console.error('[free-shipping-upsell]', data.message, data.description);
        return;
      }
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 2000);
      this.dispatchEvent(
        new CartAddEvent({}, `free-shipping-upsell-${variantId}`, {
          source: 'free-shipping-upsell',
          itemCount: 1,
          productId: card.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('[free-shipping-upsell]', error);
    } finally {
      button.disabled = false;
    }
  }
}

customElements.get('free-shipping-upsell') || customElements.define('free-shipping-upsell', FreeShippingUpsell);